import { useCallback, useEffect, useLayoutEffect, useRef, type FocusEvent, type KeyboardEvent, type RefObject } from 'react'

/**
 * The keyboard contract of an ARIA `grid`: one tab stop, arrow keys between
 * cells.
 *
 * **Roving tabindex, kept in the DOM.** Exactly one `[data-cell]` has
 * `tabindex="0"`; every other cell, and every link, button or input inside one,
 * has `-1`. Tab therefore enters the grid once and leaves it once, instead of
 * visiting every control in a 100-row page. The interactive elements inside the
 * ACTIVE cell stay tabbable, so Tab from the cell reaches them.
 *
 * The bookkeeping is imperative because the cells' content is whatever the
 * caller's column renderers return: a link, a button or a checkbox this hook
 * never sees as a prop. It runs after every render, which keeps it right when
 * the rows change under it, and again on a `MutationObserver` for a cell child
 * that mounts a control on its own.
 *
 * Cells are addressed by `data-cell="r:c"`, 0-based, with the header as row 0. A
 * row without a `data-cell` — a day band, an open row's detail panel — is
 * skipped by the arrows, because it holds nothing focusable.
 *
 * **The active cell is a position, not an element.** After a page change the
 * rows are new elements; the cell at the same row index becomes the tab stop,
 * and if focus was inside the grid it is moved there, rather than falling to
 * `<body>`.
 *
 * jsdom cannot show whether the browser's own Tab order honours this; that is
 * a real-browser check.
 */
export type RovingGridOptions = { rows: number; cols: number; enabled?: boolean }

export type RovingGridHandlers = {
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void
  onFocus: (e: FocusEvent<HTMLElement>) => void
  onBlur: (e: FocusEvent<HTMLElement>) => void
}

/**
 * Not `FOCUSABLE`: that one deliberately EXCLUDES `tabindex="-1"` (it answers
 * "what does Tab reach"), and this has to find the elements it parked at -1 in
 * order to hand them back.
 */
const INTERACTIVE = 'a[href],button,input,select,textarea,[tabindex]'

/**
 * Controls that use the arrow keys, Home and End themselves. A checkbox and the
 * button-like inputs do not, so the grid's own selection boxes stay navigable;
 * radio and range do, and stay in.
 */
const EDITABLE =
  'input:not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="image"]),' +
  'select,textarea,[contenteditable]:not([contenteditable="false"])'

/**
 * A cell whose ONLY interactive content is a checkbox hands the tab stop to the
 * checkbox. The cell and the box fill the same area, so leaving both at `0` was
 * two tab stops in one place: Tab stopped on the cell, then on the box, and the
 * grid was not the single stop it claims to be.
 */
const delegateOf = (cell: HTMLElement): HTMLElement | null => {
  const inner = cell.querySelectorAll<HTMLElement>(INTERACTIVE)
  const only = inner.length === 1 ? inner[0] : undefined
  return only?.matches('input[type="checkbox"]') ? only : null
}

const clamp = (n: number, max: number) => Math.min(Math.max(n, 0), Math.max(max, 0))

function parse(cell: string | undefined): { r: number; c: number } | null {
  const m = /^(\d+):(\d+)$/.exec(cell ?? '')
  return m ? { r: Number(m[1]), c: Number(m[2]) } : null
}

export function useRovingGrid(
  containerRef: RefObject<HTMLElement | null>,
  { rows, cols, enabled = true }: RovingGridOptions,
): RovingGridHandlers {
  const active = useRef({ r: 0, c: 0 })
  const focusWithin = useRef(false)

  const find = useCallback(
    (r: number, c: number) =>
      containerRef.current?.querySelector<HTMLElement>(`[data-cell="${r}:${c}"]`) ?? null,
    [containerRef],
  )

  /** Focus lands where the tab stop is: on the checkbox of a delegating cell, else the cell. */
  const focusAt = useCallback(
    (r: number, c: number) => {
      const cell = find(r, c)
      if (cell) (delegateOf(cell) ?? cell).focus()
    },
    [find],
  )

  const apply = useCallback(() => {
    const root = containerRef.current
    if (!root) return
    const r = clamp(active.current.r, rows - 1)
    const c = clamp(active.current.c, cols - 1)
    active.current = { r, c }
    const key = `${r}:${c}`
    for (const cell of root.querySelectorAll<HTMLElement>('[data-cell]')) {
      const on = cell.dataset.cell === key
      cell.tabIndex = on && !delegateOf(cell) ? 0 : -1
      for (const el of cell.querySelectorAll<HTMLElement>(INTERACTIVE)) {
        // An author's deliberate `tabindex="-1"` stays out of the roving set.
        // Ours carry `data-roving`, so a -1 without it is the author's.
        if (el.getAttribute('tabindex') === '-1' && !el.hasAttribute('data-roving')) continue
        el.setAttribute('data-roving', '')
        el.tabIndex = on ? 0 : -1
      }
    }
  }, [containerRef, rows, cols])

  // After every render, not on a dependency list: the caller's cells are new
  // elements whenever the rows change, and each one arrives with no tabindex.
  useLayoutEffect(() => {
    if (!enabled) return
    apply()
    const root = containerRef.current
    if (focusWithin.current && root && !root.contains(document.activeElement)) {
      focusAt(active.current.r, active.current.c)
    }
  })

  // A cell child that loads on its own (a chip with its own query) mounts a
  // control without the grid re-rendering. Childlist only: `apply` writes
  // attributes, which must not re-trigger it.
  useEffect(() => {
    const root = containerRef.current
    if (!enabled || !root || typeof MutationObserver === 'undefined') return
    const mo = new MutationObserver(apply)
    mo.observe(root, { childList: true, subtree: true })
    return () => mo.disconnect()
  }, [enabled, containerRef, apply])

  const moveTo = useCallback(
    (r: number, c: number) => {
      active.current = { r: clamp(r, rows - 1), c: clamp(c, cols - 1) }
      apply()
      focusAt(active.current.r, active.current.c)
    },
    [apply, focusAt, rows, cols],
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (!enabled || e.altKey || e.metaKey) return
      if ((e.target as HTMLElement).closest(EDITABLE)) return
      const here = parse((e.target as HTMLElement).closest<HTMLElement>('[data-cell]')?.dataset.cell)
      if (!here) return
      const ctrl = e.ctrlKey
      let next: { r: number; c: number } | null = null
      switch (e.key) {
        case 'ArrowDown': next = { r: here.r + 1, c: here.c }; break
        case 'ArrowUp': next = { r: here.r - 1, c: here.c }; break
        case 'ArrowRight': next = { r: here.r, c: here.c + 1 }; break
        case 'ArrowLeft': next = { r: here.r, c: here.c - 1 }; break
        case 'Home': next = ctrl ? { r: 0, c: 0 } : { r: here.r, c: 0 }; break
        case 'End': next = ctrl ? { r: rows - 1, c: cols - 1 } : { r: here.r, c: cols - 1 }; break
        default: return
      }
      e.preventDefault()
      moveTo(next.r, next.c)
    },
    [enabled, moveTo, rows, cols],
  )

  const onFocus = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      // React focus events bubble through portals: a dialog opened from a cell
      // reports focus here though it is not in the grid's DOM.
      if (!enabled || !containerRef.current?.contains(e.target as Node)) return
      focusWithin.current = true
      // A click or Tab into a control makes ITS cell the tab stop.
      const there = parse((e.target as HTMLElement).closest<HTMLElement>('[data-cell]')?.dataset.cell)
      if (there && (there.r !== active.current.r || there.c !== active.current.c)) {
        active.current = there
        apply()
      }
    },
    [enabled, apply, containerRef],
  )

  const onBlur = useCallback((e: FocusEvent<HTMLElement>) => {
    // A focused element removed by a re-render reports a blur on an element that
    // is no longer connected. That is not the user leaving, and the layout
    // effect above must still restore focus.
    const next = e.relatedTarget as Node | null
    if ((e.target as HTMLElement).isConnected && !(next && containerRef.current?.contains(next))) {
      focusWithin.current = false
    }
  }, [containerRef])

  return { onKeyDown, onFocus, onBlur }
}
